import { type RoomMember, type User, type Room, type ResizeMethod } from "matrix-js-sdk/src/matrix";

import DMRoomMap from "./utils/DMRoomMap";
import { mediaFromMxc } from "./customisations/Media";
import { isLocalRoom } from "./utils/localRoom/isLocalRoom";
import { getFirstGrapheme } from "./utils/strings";
import ThemeWatcher from "./settings/watchers/ThemeWatcher";
import { useIdColorHash } from "./components/views/avatars/customAvatar/MyAvatar";

/**
 * Hardcoded from the Compound colors.
 * Shade for background as defined in the compound web implementation
 * https://github.com/vector-im/compound-web/blob/main/src/components/Avatar
 */
// Ordine e semantica combaciano con _MyAvatar.pcss
// 1: blue, 2: fuchsia, 3: green, 4: pink, 5: orange, 6: cyan, 7: purple, 8: lime
// Compound HEX values (light theme)
const AVATAR_BG_LIGHT_COLORS = [
    "#e9f2ff",   // 1 blue
    "#faeefb",   // 2 fuchsia
    "#e3f7ed",   // 3 green
    "#ffecf0",   // 4 pink
    "#ffefe4",   // 5 orange
    "#e3f5f8",   // 6 cyan
    "#f1efff",   // 7 purple
    "#e0f8d9",   // 8 lime
];
const AVATAR_TEXT_LIGHT_COLORS = [
    "#043894",   // 1 blue
    "#671481",   // 2 fuchsia
    "#004933",   // 3 green
    "#7e0642",   // 4 pink
    "#850000",   // 5 orange
    "#004077",   // 6 cyan
    "#4c05b5",   // 7 purple
    "#004b00",   // 8 lime
];

// Compound HEX values (dark theme)
const AVATAR_BG_DARK_COLORS = [
    "#001264",   // 1 blue
    "#37004e",   // 2 fuchsia
    "#002513",   // 3 green
    "#450018",   // 4 pink
    "#470000",   // 5 orange
    "#001b4e",   // 6 cyan
    "#22006a",   // 7 purple
    "#002600",   // 8 lime
];
const AVATAR_TEXT_DARK_COLORS = [
    "#a1c4f8",   // 1 blue
    "#e5b1e9",   // 2 fuchsia
    "#72d5ae",   // 3 green
    "#ffabbe",   // 4 pink
    "#fbb37e",   // 5 orange
    "#78d0dc",   // 6 cyan
    "#c4baff",   // 7 purple
    "#77d94f",   // 8 lime
];

// Not to be used for BaseAvatar urls as that has similar default avatar fallback already
export function avatarUrlForMember(
    member: RoomMember | undefined,
    width: number,
    height: number,
    resizeMethod: ResizeMethod,
): string | null {
    let url: string | null = null;
    if (member?.getMxcAvatarUrl()) {
        url = mediaFromMxc(member.getMxcAvatarUrl()).getThumbnailOfSourceHttp(width, height, resizeMethod);
    }
    // Preferisco o l'url dell'immagine o direttamente il colore di sfondo, 
    // senza gestire l'avatar url di default, in modo da avere il colore sensibile al cambio tema (con l'url non potrei farlo)
    // if (!url) {
    //     // member can be null here currently since on invites, the JS SDK
    //     // does not have enough info to build a RoomMember object for
    //     // the inviter.
    //     url = defaultAvatarUrlForString(member ? member.userId : "");
    // }
    return url;
}

/**
 * Determines if the current theme is dark
 */
function isDarkTheme(): boolean {
    return new ThemeWatcher().getEffectiveTheme() === "dark";
}

/**
 * Legge il valore di una CSS variable dal document.body
 * @param variable nome della variabile CSS (es: --avatar-bg-variant1-color)
 * @returns valore della variabile o null se non trovata
 */
export function getCssVariableValue(variable: string): string | null {
    const value = getComputedStyle(document.body).getPropertyValue(variable).trim();
    return value || null;
}

/**
 * Determina il colore HEX da usare per lo sfondo dell'avatar, leggendo dal CSS se possibile
 * @param id the user or room ID
 * @returns il colore di sfondo da usare sull'avatar
 */
export function getAvatarBackgroundColor(id: string): string {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const index = useIdColorHash(id); // 1-based
    const defaultColor = isDarkTheme() ? AVATAR_BG_DARK_COLORS[index - 1] : AVATAR_BG_LIGHT_COLORS[index - 1];
    const cssVar = `--avatar-bg-variant${index}-color`;
    // devo tornare la var invece del colore risolto perché altrimenti non si aggiorna dinamicamente al cambio tema
    return `var(${cssVar}, ${defaultColor})`;
}

/**
 * Determina il colore HEX da usare per il testo dell'avatar, leggendo dal CSS se possibile
 * @param id the user or room ID
 * @returns il colore testo da usare sull'avatar
 */
export function getAvatarTextColor(id: string): string {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const index = useIdColorHash(id); // 1-based
    const defaultColor = isDarkTheme() ? AVATAR_TEXT_DARK_COLORS[index - 1] : AVATAR_TEXT_LIGHT_COLORS[index - 1];
    const cssVar = `--avatar-color-variant${index}-color`;
    // devo tornare la var invece del colore risolto perché altrimenti non si aggiorna dinamicamente al cambio tema
    return `var(${cssVar}, ${defaultColor})`;
}
// /**
//  * Determina il colore HEX da usare per il testo dell'avatar, leggendo dal CSS se possibile
//  * @param id the user or room ID
//  * @returns il colore testo da usare sull'avatar
//  */
// export function getAvatarTextColor(id: string): string {
//     // eslint-disable-next-line react-hooks/rules-of-hooks
//     const index = useIdColorHash(id);

//     // Use light colors by default
//     return isDarkTheme() ? AVATAR_TEXT_DARK_COLORS[index - 1] : AVATAR_TEXT_LIGHT_COLORS[index - 1];
// }

export function avatarUrlForUser(
    user: Pick<User, "avatarUrl">,
    width: number,
    height: number,
    resizeMethod?: ResizeMethod,
): string | null {
    if (!user.avatarUrl) return null;
    return mediaFromMxc(user.avatarUrl).getThumbnailOfSourceHttp(width, height, resizeMethod);
}

export function isValidHexColor(color: string): boolean {
    return (
        typeof color === "string" &&
        (color.length === 7 || color.length === 9) &&
        color.startsWith("#") &&
        !color
            .slice(1)
            .split("")
            .some((c) => isNaN(parseInt(c, 16)))
    );
}

// COMMENTATO perchè Non voglio gestire l'avatar Url di default, 
// o viene usato l'immagine url associata altrimenti direttamente il colore di sfondo
//#region COMMENTATA GESTIONE AavatarUrl
// function urlForColor(color: string): string {
//     const size = 40;
//     const canvas = document.createElement("canvas");
//     canvas.width = size;
//     canvas.height = size;
//     const ctx = canvas.getContext("2d");
//     // bail out when using jsdom in unit tests
//     if (!ctx) {
//         return "";
//     }
//     ctx.fillStyle = color;
//     ctx.fillRect(0, 0, size, size);
//     return canvas.toDataURL();
// }

// XXX: Ideally we'd clear this cache when the theme changes
// but since this function is at global scope, it's a bit
// hard to install a listener here, even if there were a clear event to listen to
// const colorToDataURLCache = new Map<string, string>();

// export function defaultAvatarUrlForString(s: string): string {
//     if (!s) return ""; // XXX: should never happen but empirically does by evidence of a rageshake
//     // eslint-disable-next-line react-hooks/rules-of-hooks
//     const colorIndex = useIdColorHash(s);
//     // overwritten color value in custom themes
//     const cssVariable = `--avatar-background-colors_${colorIndex}`;
//     const cssValue = getComputedStyle(document.body).getPropertyValue(cssVariable);
//     // Light colors are the default
//     const color =
//         cssValue || isDarkTheme() ? AVATAR_BG_DARK_COLORS[colorIndex - 1] : AVATAR_BG_LIGHT_COLORS[colorIndex - 1];

//     let dataUrl = colorToDataURLCache.get(color);
//     if (!dataUrl) {
//         // validate color as this can come from account_data
//         // with custom theming
//         if (isValidHexColor(color)) {
//             dataUrl = urlForColor(color);
//             colorToDataURLCache.set(color, dataUrl);
//         } else {
//             dataUrl = "";
//         }
//     }
//     return dataUrl;
// }
//#endregion

/**
 * returns the first (non-sigil) character of 'name',
 * converted to uppercase
 * @param {string} name
 * @return {string} the first letter
 */
export function getInitialLetter(name: string): string | undefined {
    if (!name) {
        // XXX: We should find out what causes the name to sometimes be falsy.
        // console.trace("`name` argument to `getInitialLetter` not supplied");
        return undefined;
    }
    if (name.length < 1) {
        return undefined;
    }

    const initial = name[0];
    if ((initial === "@" || initial === "#" || initial === "+") && name[1]) {
        name = name.substring(1);
    }

    return getFirstGrapheme(name).toUpperCase();
}

export function avatarUrlForRoom(
    room: Room | null,
    width?: number,
    height?: number,
    resizeMethod?: ResizeMethod,
    avatarMxcOverride?: string,
): string | null {
    if (!room) return null; // null-guard
    const mxc = avatarMxcOverride ?? room.getMxcAvatarUrl();
    if (mxc) {
        const media = mediaFromMxc(mxc);
        if (width !== undefined && height !== undefined) {
            return media.getThumbnailOfSourceHttp(width, height, resizeMethod);
        }
        return media.srcHttp;
    }

    // space rooms cannot be DMs so skip the rest
    if (room.isSpaceRoom()) return null;

    // If the room is not a DM don't fallback to a member avatar
    if (!DMRoomMap.shared().getUserIdForRoomId(room.roomId) && !isLocalRoom(room)) {
        return null;
    }

    // If there are only two members in the DM use the avatar of the other member
    const otherMember = room.getAvatarFallbackMember();
    if (otherMember?.getMxcAvatarUrl()) {
        const media = mediaFromMxc(otherMember.getMxcAvatarUrl());
        if (width !== undefined && height !== undefined) {
            return media.getThumbnailOfSourceHttp(width, height, resizeMethod);
        }
        return media.srcHttp;
    }
    return null;
}